/**
 * Conflict Resolution Service
 * Handles merging and resolving conflicts between local and server versions of contracts
 */

import { offlineStorage } from './offlineStorage';
import DiffMatchPatch from 'diff-match-patch';

// Initialize diff-match-patch
const dmp = new DiffMatchPatch();

// Conflict types
export const CONFLICT_TYPES = {
  CONTENT_MODIFIED: 'content_modified',
  TITLE_MODIFIED: 'title_modified',
  STATUS_CHANGED: 'status_changed',
  DELETED_ON_SERVER: 'deleted_on_server',
  CONCURRENT_EDIT: 'concurrent_edit'
};

// Resolution strategies
export const RESOLUTION_STRATEGIES = {
  KEEP_LOCAL: 'keep_local',
  KEEP_SERVER: 'keep_server',
  MERGE: 'merge',
  MANUAL: 'manual'
};

class ConflictResolver {
  constructor() {
    this.pendingConflicts = new Map();
    this.listeners = new Set();
  }

  /**
   * Compare local and server versions to detect conflicts
   */
  detectConflicts(localContract, serverContract) {
    const conflicts = [];

    if (!serverContract) {
      // Server contract was deleted
      conflicts.push({
        type: CONFLICT_TYPES.DELETED_ON_SERVER,
        localValue: localContract,
        serverValue: null,
        field: 'document'
      });
      return conflicts;
    }

    // Check if local has pending changes
    if (localContract.sync_status !== 'synced') {
      // Check content differences
      if (localContract.content !== serverContract.content) {
        conflicts.push({
          type: CONFLICT_TYPES.CONTENT_MODIFIED,
          localValue: localContract.content,
          serverValue: serverContract.content,
          localTimestamp: localContract._local_edit_at,
          serverTimestamp: serverContract.updated_at,
          field: 'content'
        });
      }

      // Check title differences
      if (localContract.title !== serverContract.title) {
        conflicts.push({
          type: CONFLICT_TYPES.TITLE_MODIFIED,
          localValue: localContract.title,
          serverValue: serverContract.title,
          field: 'title'
        });
      }

      // Check status changes
      if (localContract.status !== serverContract.status) {
        conflicts.push({
          type: CONFLICT_TYPES.STATUS_CHANGED,
          localValue: localContract.status,
          serverValue: serverContract.status,
          field: 'status'
        });
      }
    }

    return conflicts;
  }

  /**
   * Auto-resolve conflicts based on strategy
   */
  autoResolve(conflicts, strategy = RESOLUTION_STRATEGIES.KEEP_SERVER) {
    const resolved = {};

    for (const conflict of conflicts) {
      switch (strategy) {
        case RESOLUTION_STRATEGIES.KEEP_LOCAL:
          resolved[conflict.field] = conflict.localValue;
          break;
        case RESOLUTION_STRATEGIES.KEEP_SERVER:
          resolved[conflict.field] = conflict.serverValue;
          break;
        case RESOLUTION_STRATEGIES.MERGE:
          resolved[conflict.field] = this.mergeValues(conflict);
          break;
        default:
          // Manual resolution needed
          break;
      }
    }

    return resolved;
  }

  /**
   * Merge two versions of content
   */
  mergeValues(conflict) {
    if (conflict.field === 'content') {
      return this.mergeContent(conflict.localValue, conflict.serverValue);
    }
    
    // For other fields, prefer the most recent
    const localTime = new Date(conflict.localTimestamp || 0).getTime();
    const serverTime = new Date(conflict.serverTimestamp || 0).getTime();
    
    return localTime > serverTime ? conflict.localValue : conflict.serverValue;
  }

  /**
   * Advanced three-way merge for text content using diff-match-patch
   */
  mergeContent(local, server) {
    // If one is empty, use the other
    if (!local) return server;
    if (!server) return local;
    if (local === server) return local;

    try {
      // Compute the diff between server (base) and local (modified)
      const diffs = dmp.diff_main(server, local);
      dmp.diff_cleanupSemantic(diffs);

      // Check if there are actual differences
      const hasChanges = diffs.some(d => d[0] !== 0);
      
      if (!hasChanges) {
        return local;
      }

      // Create a merged result with clear markers
      let merged = '';
      let hasConflictMarkers = false;
      
      for (const [op, text] of diffs) {
        if (op === 0) {
          // Unchanged text
          merged += text;
        } else if (op === -1) {
          // Deleted from server (local doesn't have this)
          // Mark as server-only content
          if (text.trim()) {
            merged += `\n<<<< SERVIDOR >>>>\n${text}\n<<<< FIN SERVIDOR >>>>\n`;
            hasConflictMarkers = true;
          }
        } else if (op === 1) {
          // Added in local (server doesn't have this)
          // Mark as local-only content
          if (text.trim()) {
            merged += `\n<<<< LOCAL >>>>\n${text}\n<<<< FIN LOCAL >>>>\n`;
            hasConflictMarkers = true;
          }
        }
      }

      // If no conflict markers were added, return the cleaner version
      if (!hasConflictMarkers) {
        // Use patch approach for cleaner merge
        const patches = dmp.patch_make(server, diffs);
        const [patchedText, results] = dmp.patch_apply(patches, server);
        
        // Check if all patches applied successfully
        if (results.every(r => r)) {
          return patchedText;
        }
      }

      // Clean up excessive newlines
      merged = merged.replace(/\n{3,}/g, '\n\n');
      
      return merged.trim();
    } catch (error) {
      console.error('Merge failed, using fallback:', error);
      // Fallback: simple concatenation with markers
      return `<<<< VERSIÓN LOCAL >>>>\n${local}\n\n<<<< VERSIÓN SERVIDOR >>>>\n${server}\n<<<< FIN CONFLICTO >>>>`;
    }
  }

  /**
   * Create semantic patches between two versions
   */
  createPatches(original, modified) {
    const diffs = dmp.diff_main(original, modified);
    dmp.diff_cleanupSemantic(diffs);
    return dmp.patch_make(original, diffs);
  }

  /**
   * Apply patches to text
   */
  applyPatches(text, patches) {
    const [patchedText, results] = dmp.patch_apply(patches, text);
    return {
      text: patchedText,
      success: results.every(r => r),
      applied: results.filter(r => r).length,
      total: results.length
    };
  }

  /**
   * Create a detailed diff view between two versions
   */
  createDiffView(local, server) {
    if (!local && !server) return [];
    if (!local) local = '';
    if (!server) server = '';

    const diffs = dmp.diff_main(server, local);
    dmp.diff_cleanupSemantic(diffs);

    const result = [];
    let lineNumber = 1;

    for (const [op, text] of diffs) {
      const lines = text.split('\n');
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const isLastLine = i === lines.length - 1;
        
        if (op === 0) {
          // Unchanged
          result.push({
            type: 'unchanged',
            content: line,
            line: lineNumber
          });
        } else if (op === -1) {
          // Removed (was in server, not in local)
          result.push({
            type: 'removed',
            content: line,
            line: lineNumber,
            source: 'server'
          });
        } else if (op === 1) {
          // Added (in local, not in server)
          result.push({
            type: 'added',
            content: line,
            line: lineNumber,
            source: 'local'
          });
        }

        if (!isLastLine) {
          lineNumber++;
        }
      }
    }

    return result;
  }

  /**
   * Create HTML diff view for display
   */
  createHtmlDiff(local, server) {
    if (!local) local = '';
    if (!server) server = '';

    const diffs = dmp.diff_main(server, local);
    dmp.diff_cleanupSemantic(diffs);

    return diffs.map(([op, text]) => {
      const escapedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');

      if (op === 0) {
        return `<span class="diff-unchanged">${escapedText}</span>`;
      } else if (op === -1) {
        return `<span class="diff-removed">${escapedText}</span>`;
      } else {
        return `<span class="diff-added">${escapedText}</span>`;
      }
    }).join('');
  }

  /**
   * Get diff statistics
   */
  getDiffStats(local, server) {
    if (!local) local = '';
    if (!server) server = '';

    const diffs = dmp.diff_main(server, local);
    
    let added = 0;
    let removed = 0;
    let unchanged = 0;

    for (const [op, text] of diffs) {
      if (op === 0) unchanged += text.length;
      else if (op === 1) added += text.length;
      else if (op === -1) removed += text.length;
    }

    return {
      added,
      removed,
      unchanged,
      total: added + removed + unchanged,
      similarity: unchanged / (added + removed + unchanged) * 100
    };
  }

  /**
   * Register a conflict for manual resolution
   */
  registerConflict(contractId, conflicts) {
    this.pendingConflicts.set(contractId, {
      conflicts,
      registeredAt: new Date().toISOString()
    });
    this.notifyListeners({ type: 'conflict_detected', contractId, conflicts });
  }

  /**
   * Get pending conflicts for a contract
   */
  getPendingConflicts(contractId) {
    return this.pendingConflicts.get(contractId);
  }

  /**
   * Get all pending conflicts
   */
  getAllPendingConflicts() {
    return Array.from(this.pendingConflicts.entries()).map(([id, data]) => ({
      contractId: id,
      ...data
    }));
  }

  /**
   * Resolve a conflict manually
   */
  async resolveConflict(contractId, resolution) {
    const pending = this.pendingConflicts.get(contractId);
    if (!pending) return false;

    // Apply resolution
    const contract = await offlineStorage.getContract(contractId);
    if (contract) {
      Object.assign(contract, resolution);
      contract.sync_status = 'resolved';
      contract._conflict_resolved_at = new Date().toISOString();
      await offlineStorage.saveContract(contract);
    }

    // Remove from pending
    this.pendingConflicts.delete(contractId);
    this.notifyListeners({ type: 'conflict_resolved', contractId });

    return true;
  }

  /**
   * Add listener for conflict events
   */
  addListener(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event) {
    this.listeners.forEach(callback => {
      try {
        callback(event);
      } catch (e) {
        console.error('Conflict listener error:', e);
      }
    });
  }

  /**
   * Check if there are any pending conflicts
   */
  hasPendingConflicts() {
    return this.pendingConflicts.size > 0;
  }

  /**
   * Get conflict count
   */
  getConflictCount() {
    return this.pendingConflicts.size;
  }
}

// Export singleton instance
export const conflictResolver = new ConflictResolver();
export default conflictResolver;
